import React from "react";
import { Box, Typography, useTheme, Avatar } from "@mui/material";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Pencil,
  Phone,
} from "lucide-react";
import { DeveloperTrack } from "./types";
import StageStepper from "./stage-stepper";
import WhatsappIcon from "@/assets/icons/lead-page/whatsapp-icon";
import {
  handleContactWhatsapp,
  handleContactPhone,
} from "@/modules/common/utils";

interface DeveloperTrackCardProps {
  track: DeveloperTrack;
  onViewDetails?: () => void;
  onEditUser?: () => void;
  onUpdateStatus?: () => void;
  isDetailView?: boolean;
}

const DeveloperTrackCard: React.FC<DeveloperTrackCardProps> = ({
  track,
  onViewDetails,
  onEditUser,
  onUpdateStatus,
  isDetailView = false,
}) => {
  const theme = useTheme();

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
        borderRadius: theme.nawy.radius.xl,
        overflow: "hidden",
        border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header - Accordion Header */}
      <Box
        onClick={handleViewDetails}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.nawy.spacing.md,
          padding: theme.nawy.spacing.md,
          cursor: "pointer",
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          "&:hover": {
            backgroundColor: theme.nawy.colors.semantic.layer.secondary,
          },
        }}
      >
        <Typography
          sx={{
            flex: 1,
            ...theme.nawy.typography.english.variants.body.lg.semibold,
            color: theme.nawy.colors.semantic.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {track.developerName} details
        </Typography>
        {isDetailView ? (
          <ChevronUp
            size={20}
            color={theme.nawy.colors.semantic.text.primary}
          />
        ) : (
          <ChevronRight
            size={20}
            color={theme.nawy.colors.semantic.text.primary}
          />
        )}
      </Box>

      {/* Details Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.sm,
          padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          flex: 1,
        }}
      >
        {/* User Card */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.sm,
            padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            borderRadius: theme.nawy.radius.xl,
            overflow: "hidden",
          }}
        >
          {/* Top Line - Developer Info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.nawy.spacing.sm,
              width: "100%",
            }}
          >
            {/* Developer Logo */}
            <Avatar
              sx={{
                width: "44px",
                height: "44px",
                borderRadius: theme.nawy.radius.lg,
                border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
                backgroundColor: theme.nawy.colors.semantic.layer.secondary,
                fontSize: "10px",
                fontWeight: 500,
                color: theme.nawy.colors.semantic.text.secondary,
              }}
              variant="rounded"
            >
              {track.developerLogo ? (
                <img
                  src={track.developerLogo}
                  alt={track.developerName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                track.developerName.substring(0, 3).toLowerCase()
              )}
            </Avatar>

            {/* Developer Name & Agent */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.xxxs,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                {track.developerName}
              </Typography>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.secondary,
                }}
              >
                {track.agentName}
              </Typography>
            </Box>

            {/* Edit Button */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onEditUser?.();
              }}
              sx={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.nawy.colors.semantic.layer.primary,
                border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
                borderRadius: theme.nawy.radius.lg,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.nawy.colors.semantic.layer.secondary,
                },
              }}
            >
              <Pencil
                size={16}
                color={theme.nawy.colors.semantic.text.primary}
              />
            </Box>
          </Box>

          {/* Phone Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "36px",
              padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.md}`,
              backgroundColor: theme.nawy.colors.primitives.white[200],
              borderRadius: theme.nawy.radius.lg,
            }}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {track.phoneNumber}
            </Typography>

            {/* Action Icons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.nawy.spacing.xs,
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleContactPhone(track.phoneNumber);
                }}
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <Phone
                  size={16}
                  color={theme.nawy.colors.semantic.text.primary}
                />
              </Box>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleContactWhatsapp(track.phoneNumber);
                }}
                sx={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
              >
                <WhatsappIcon fill={theme.nawy.colors.semantic.text.primary} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Stage Stepper */}
      <StageStepper stages={track.stages} />

      {/* Status Button */}
      <Box sx={{ padding: theme.nawy.spacing.sm }}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onUpdateStatus?.();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.xxs,
            padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
            backgroundColor:
              track.currentStatusColor ||
              theme.nawy.colors.primitives.grey[100],
            borderRadius: theme.nawy.radius.lg,
            cursor: "pointer",
            transition: "filter 0.2s ease",
            "&:hover": {
              filter: "brightness(0.95)",
            },
          }}
        >
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.primitives.white[100],
              textAlign: "center",
            }}
          >
            {track.currentStatus}
          </Typography>
          <ChevronDown
            size={16}
            color={theme.nawy.colors.semantic.text.primary}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DeveloperTrackCard;
